// MoveAssetsToAnotherTypeDialog.tsx

import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useTranslation } from '@/lib';
import { assetService } from '@/modules/assets/api/assets/asset.service';
import { AssetTypeModel } from '@/modules/asset-types/types/AssetTypeModel';
import AssetTypeSelect from '@/modules/asset-types/components/AssetTypeSelect';

interface ChangeAssetTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetsMoved: () => void;
  selectedAssetIds: number[];
}

const ChangeAssetTypeModal: FunctionComponent<ChangeAssetTypeModalProps> = ({ isOpen, onClose, onAssetsMoved, selectedAssetIds }) => {
  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeModel | undefined>(undefined);
  const [isMovingAssets, setIsMovingAssets] = useState(false);
  const { t } = useTranslation();

  async function onMoveAssetsClicked() {
    if (!selectedAssetType) return;

    setIsMovingAssets(true);

    // Move assets to the new asset type
    const response = await assetService.bulkUpdate({ assetTypeId: selectedAssetType.id, assetIds: selectedAssetIds });

    if (!response.isSuccess) {
      toast.error(t('failed_to_move_assets_try_again'));
      setIsMovingAssets(false);
      return;
    }

    // Now that the assets have been moved, close the dialog
    onAssetsMoved();
    onClose();

    setIsMovingAssets(false);
  }

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{t('move_assets_to_another_type')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('move_selected_assets_to_another_type', { count: selectedAssetIds.length })}</DialogContentText>
        <div className="mt-4">
          <AssetTypeSelect selectedAssetTypeId={selectedAssetType?.id} onChange={setSelectedAssetType} label={t('select_new_asset_type')} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton loading={isMovingAssets} variant="contained" onClick={onMoveAssetsClicked} disabled={!selectedAssetType}>
          {t('move_assets')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeAssetTypeModal;
